import { Box, Stack } from "@mui/material"
import { MoreHorizOutlined } from "@mui-symbols-material/w300"
import type { KeyboardEvent, MouseEvent, ReactElement, ReactNode } from "react"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { AerosTag } from "./AerosTag"

export interface AerosTagData {
  key: number | string
  label: string
  value?: number | string
  icon?: ReactElement
}

export interface AerosTagStackProps {
  disabled?: boolean
  tags: AerosTagData[]
  onDelete?: (tagToDelete: AerosTagData) => void
  children?: ReactNode
  className?: string
  shouldWrap?: boolean
}

export const AerosTagStack = ({
  disabled = false,
  tags = [],
  onDelete,
  children,
  className,
  shouldWrap = false,
}: AerosTagStackProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleTags, setVisibleTags] = useState<AerosTagData[]>([])
  const [isOverflowing, setIsOverflowing] = useState(false)

  const updateVisibleTags = useCallback(() => {
    if (!measureRef.current || !containerRef.current) return

    const containerWidth = containerRef.current.clientWidth - 40 // Reserve space for overflow indicator
    const gapWidth = 8 // gap-2 = 0.5rem = 8px
    let totalWidth = 0
    const visibleTagsArray: AerosTagData[] = []

    Array.from(measureRef.current.children).forEach((tag, index) => {
      const tagWidth = (tag as HTMLElement).offsetWidth
      const widthWithGap = totalWidth > 0 ? tagWidth + gapWidth : tagWidth

      if (totalWidth + widthWithGap <= containerWidth) {
        visibleTagsArray.push(tags[index])
        totalWidth += widthWithGap
      }
    })

    // When a chip is wider than (containerWidth - 40) the loop above pushes nothing,
    // producing an empty visibleTags and showing only the "…" indicator with no delete
    // handler — making the selection unremovable from the field. Always show at least
    // the first tag so the user can always delete it. When it's the only tag,
    // isOverflowing becomes false and the chip reclaims the 40 px reservation.
    if (!visibleTagsArray.length && tags.length) {
      visibleTagsArray.push(tags[0])
    }

    setVisibleTags(visibleTagsArray)
    setIsOverflowing(visibleTagsArray.length < tags.length)
  }, [tags])

  useLayoutEffect(() => {
    updateVisibleTags()
  }, [tags, updateVisibleTags])

  const handleDelete = useCallback(
    (tagToDelete: AerosTagData) => {
      if (disabled) return

      onDelete?.(tagToDelete)
    },
    [disabled, onDelete]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent, tag: AerosTagData) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault()
        event.stopPropagation()
        handleDelete(tag)
      }
    },
    [handleDelete]
  )

  const handleTagMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }, [])

  const renderTag = (tag: AerosTagData) => (
    <AerosTag
      key={tag.key}
      icon={tag.icon}
      label={tag.label}
      disabled={disabled}
      onDelete={disabled ? undefined : () => handleDelete(tag)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={handleTagMouseDown}
      onKeyDown={(e) => handleKeyDown(e, tag)}
    />
  )

  // Visible chips get label truncation so the delete button is never clipped by the
  // container's overflow:hidden — even when a label is wider than the full field width.
  // The measure container uses renderTag (natural widths) to keep calculations accurate.
  const renderVisibleTag = (tag: AerosTagData) => (
    <AerosTag
      key={tag.key}
      icon={tag.icon}
      label={tag.label}
      disabled={disabled}
      onDelete={disabled ? undefined : () => handleDelete(tag)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={handleTagMouseDown}
      onKeyDown={(e) => handleKeyDown(e, tag)}
      sx={{
        minWidth: 0,
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    />
  )

  return (
    <Stack
      ref={containerRef}
      direction='row'
      className={twMerge("relative grow items-center gap-2", shouldWrap ? "flex-wrap" : "", className)}
    >
      {shouldWrap ? (
        tags.map(renderTag)
      ) : (
        <>
          {visibleTags.map(renderVisibleTag)}
          {isOverflowing && (
            <AerosTag
              label={<MoreHorizOutlined className='size-4' />}
              sx={{ "& .MuiChip-label": { padding: "0 0.25rem" } }}
            />
          )}
        </>
      )}
      {children && <Box className='flex-none'>{children}</Box>}
      <Box className='invisible absolute' ref={measureRef}>
        {tags.map(renderTag)}
      </Box>
    </Stack>
  )
}

AerosTagStack.displayName = "AerosTagStack"
